(() => {
  if (customElements.get('cgl-spinning-customizer')) return;

  class CglSpinningCustomizer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.product = this.readJson('[data-cgl-sc-product]') || { options:[],variants:[] };
      this.assets = this.readJson('[data-cgl-sc-assets]') || { overlays:{},thumbs:{},chains:{} };
      this.selected = {
        Pendant:this.dataset.defaultPendant || 'Circle',
        Chain:this.dataset.defaultChain || 'Rope'
      };
      this.color = this.dataset.defaultColor || 'Gold';
      this.length = this.dataset.defaultLength || '18"';
      this.pendantChosen = false;
      this.colorChosen = false;
      this.sides = ['Front','Back'];
      this.photos = this.sides.map(() => this.emptyPhoto());
      this.activeIndex = 0;
      this.loading = false;
      this.photoEditing = false;
      this.customizationId = this.makeId('cglspin');
      this.sessionId = this.getSessionId();
      this.debug = new URLSearchParams(location.search).get('cgl_debug') === '1';

      this.stage = this.querySelector('[data-cgl-sc-stage]');
      this.productIntro = this.querySelector('[data-cgl-sc-product-intro]');
      this.photoWindow = this.querySelector('[data-cgl-sc-photo-window]');
      this.photoImage = this.querySelector('[data-cgl-sc-photo]');
      this.overlay = this.querySelector('[data-cgl-sc-overlay]');
      this.chainLayer = this.querySelector('[data-cgl-sc-chain-layer]');
      this.fileInput = this.querySelector('[data-cgl-sc-file]');
      this.confirmButton = this.querySelector('[data-cgl-sc-confirm-photo]');
      this.gesture = this.querySelector('[data-cgl-sc-gesture]');
      this.recapCanvases = [...this.querySelectorAll('[data-cgl-sc-recap-side]')];
      this.dockCanvas = this.querySelector('[data-cgl-sc-dock-canvas]');
      this.lastConfirmedIndex = 0;
      this.buy = this.querySelector('[data-cgl-sc-buy]');
      this.sticky = this.querySelector('[data-cgl-sc-sticky-buy]');
      this.photoError = this.querySelector('[data-cgl-sc-photo-error]');
      this.formError = this.querySelector('[data-cgl-sc-form-error]');

      this.bind();
      this.updateVariant();
      this.updateArtwork();
      this.updateUI();
      this.initDock();
      this.initSticky();
      this.track('customizer_view');
    }

    disconnectedCallback() {
      this.photos?.forEach((photo) => photo.url && URL.revokeObjectURL(photo.url));
      this.stickyObserver?.disconnect();
      window.removeEventListener('scroll',this.onDockScroll);
      window.removeEventListener('resize',this.onDockScroll);
      if (this.dockFrame) cancelAnimationFrame(this.dockFrame);
      document.documentElement.classList.remove('cgl-sc-editor-open');
    }

    bind() {
      this.querySelectorAll('[data-cgl-sc-pendant]').forEach((button) => button.addEventListener('click',() => {
        this.selected.Pendant = button.dataset.cglScPendant;
        this.pendantChosen = true;
        this.selectGroup('[data-cgl-sc-pendant]',button);
        this.stage.dataset.pendant = this.handle(this.selected.Pendant);
        this.updateVariant(); this.updateArtwork(); this.updateUI();
        this.track('pendant_selected',{ pendant_type:this.selected.Pendant });
      }));
      this.querySelectorAll('[data-cgl-sc-color]').forEach((button) => button.addEventListener('click',() => {
        this.color = button.dataset.cglScColor;
        this.colorChosen = true;
        this.selectGroup('[data-cgl-sc-color]',button);
        this.updateArtwork(); this.updateUI();
        this.track('color_selected',{ color:this.color });
      }));
      this.querySelectorAll('[data-cgl-sc-chain]').forEach((button) => button.addEventListener('click',() => {
        this.selected.Chain = button.dataset.cglScChain;
        this.selectGroup('[data-cgl-sc-chain]',button);
        this.updateVariant(); this.updateArtwork(); this.updateUI();
        this.track('chain_selected',{ chain_type:this.selected.Chain });
      }));
      this.querySelectorAll('input[name^="cgl-sc-length-"]').forEach((input) => input.addEventListener('change',() => {
        this.length = input.value; this.updateUI(); this.track('length_selected',{ chain_length:this.length });
      }));
      this.querySelectorAll('[data-cgl-sc-slot]').forEach((button) => button.addEventListener('click',() => this.openSide(Number(button.dataset.cglScSlot))));
      this.fileInput?.addEventListener('change',() => this.acceptFile());
      this.confirmButton?.addEventListener('click',() => this.confirmPhoto());
      this.querySelector('[data-cgl-sc-reset]')?.addEventListener('click',() => this.resetCrop());
      this.querySelector('[data-cgl-sc-dock-preview]')?.addEventListener('click',() => this.querySelector('[data-cgl-sc-photos-card]')?.scrollIntoView({ behavior:'smooth',block:'start' }));
      this.querySelector('[data-cgl-sc-atc]')?.addEventListener('click',() => this.addToCart());
      this.querySelector('[data-cgl-sc-sticky-atc]')?.addEventListener('click',() => this.addToCart());
      this.stage?.addEventListener('pointerdown',(event) => this.pointerDown(event));
      this.stage?.addEventListener('pointermove',(event) => this.pointerMove(event));
      this.stage?.addEventListener('pointerup',(event) => this.pointerUp(event));
      this.stage?.addEventListener('pointercancel',(event) => this.pointerUp(event));
      this.pointers = new Map();
    }

    emptyPhoto() {
      return { file:null,url:'',image:null,production:null,productionPromise:null,confirmed:false,zoom:1,x:0,y:0,aspect:1,adjusted:false };
    }

    openSide(index) {
      if (!Number.isInteger(index) || !this.photos[index]) return;
      if (!this.pendantChosen || !this.colorChosen || (index === 1 && !this.photos[0].confirmed)) return;
      this.activeIndex = index;
      this.hideError(this.photoError);
      if (!this.photos[index].file) {
        this.track('photo_upload_started',{ photo_side:this.sides[index].toLowerCase() });
        this.fileInput.value = '';
        this.fileInput.click();
        return;
      }
      this.startEditor();
    }

    async acceptFile() {
      const file = this.fileInput?.files?.[0];
      if (!file) return;
      if (!file.type.startsWith('image/') && !/\.(heic|heif)$/i.test(file.name || '')) {
        this.showError(this.photoError,'Please choose a JPG, PNG, WebP, HEIC or HEIF photo.');
        return;
      }
      const old = this.photos[this.activeIndex];
      if (old.url) URL.revokeObjectURL(old.url);
      const photo = this.emptyPhoto();
      photo.file = file;
      photo.url = URL.createObjectURL(file);
      this.photos[this.activeIndex] = photo;
      try {
        photo.image = await this.loadImage(photo.url,false);
        photo.aspect = photo.image.naturalWidth / Math.max(1,photo.image.naturalHeight);
        photo.productionPromise = this.optimizePhoto(file,photo.image).then((optimized) => { photo.production = optimized; return optimized; });
        this.track('photo_uploaded',{ photo_side:this.sides[this.activeIndex].toLowerCase(),photos_uploaded:this.photos.filter((item) => item.file).length });
        this.startEditor();
      } catch (error) {
        this.showError(this.photoError,'This photo could not be previewed. Please try a JPG, PNG or WebP image.');
      }
    }

    startEditor() {
      const photo = this.photos[this.activeIndex];
      if (!photo?.image) return;
      photo.confirmed = false;
      this.photoEditing = true;
      this.classList.add('is-photo-editing');
      this.dataset.editingSlot = String(this.activeIndex);
      document.documentElement.classList.add('cgl-sc-editor-open');
      this.setText('[data-cgl-sc-editor-kicker]',`${this.sides[this.activeIndex].toUpperCase()} PHOTO`);
      this.setText('[data-cgl-sc-confirm-label]',`USE ${this.sides[this.activeIndex].toUpperCase()} PHOTO`);
      this.setText('[data-cgl-sc-preview-status]','Position your photo');
      this.gesture.hidden = photo.adjusted;
      this.renderActivePhoto();
      this.updateUI();
      this.updateDock?.();
      window.requestAnimationFrame(() => this.confirmButton?.focus({ preventScroll:true }));
    }

    confirmPhoto() {
      const photo = this.photos[this.activeIndex];
      if (!photo?.image) return;
      photo.confirmed = true;
      this.lastConfirmedIndex = this.activeIndex;
      this.photoEditing = false;
      this.classList.remove('is-photo-editing');
      delete this.dataset.editingSlot;
      document.documentElement.classList.remove('cgl-sc-editor-open');
      this.track('photo_confirmed',{ photo_side:this.sides[this.activeIndex].toLowerCase(),photos_confirmed:this.confirmedCount() });
      this.updateUI();
      this.renderRecap();
      this.updateDock?.();
      const next = this.photos.findIndex((item) => !item.confirmed);
      if (next >= 0) window.requestAnimationFrame(() => this.querySelector(`[data-cgl-sc-slot="${next}"]`)?.focus({ preventScroll:true }));
      else window.requestAnimationFrame(() => this.querySelector('[data-cgl-sc-step="chain"]')?.scrollIntoView({ behavior:'smooth',block:'start' }));
    }

    resetCrop() {
      const photo = this.photos[this.activeIndex];
      if (!photo) return;
      Object.assign(photo,{ zoom:1,x:0,y:0,adjusted:false });
      this.gesture.hidden = false;
      this.renderActivePhoto();
    }

    updateArtwork() {
      const overlaySource = this.assets.overlays?.[this.selected.Pendant]?.[this.color];
      if (overlaySource && this.overlay?.src !== overlaySource) this.overlay.src = overlaySource;
      const chainSource = this.assets.chains?.[this.selected.Chain]?.[this.color];
      if (this.chainLayer) {
        this.chainLayer.hidden = !chainSource;
        if (chainSource) this.chainLayer.src = chainSource;
        this.chainLayer.classList.toggle('is-silver',this.color === 'Silver');
      }
      this.querySelectorAll('[data-cgl-sc-pendant-thumb]').forEach((image) => {
        const shape = image.closest('[data-cgl-sc-pendant]')?.dataset.cglScPendant;
        const source = this.assets.thumbs?.[shape]?.[this.color];
        if (source && image.src !== source) image.src = source;
      });
      this.querySelectorAll('[data-cgl-sc-chain-thumb]').forEach((image) => {
        const source = this.assets.chainThumbs?.[image.dataset.cglScChainThumb]?.[this.color];
        if (source && image.src !== source) image.src = source;
      });
      this.renderActivePhoto();
      this.renderRecap();
    }

    updateVariant() {
      const optionNames = (this.product.options || []).map((option) => typeof option === 'string' ? option : option.name);
      const desired = { Pendant:this.selected.Pendant,Chain:this.selected.Chain };
      this.currentVariant = (this.product.variants || []).find((variant) => optionNames.every((name,index) => desired[name] == null || variant.options?.[index] === desired[name])) || null;
      if (this.currentVariant) this.dataset.variantId = this.currentVariant.id;
      this.updateUpgradeLabels(optionNames);
    }

    updateUpgradeLabels(optionNames) {
      const baseFor = (pendant,chain) => (this.product.variants || []).find((variant) => optionNames.every((name,index) => {
        if (name === 'Pendant') return variant.options?.[index] === pendant;
        if (name === 'Chain') return variant.options?.[index] === chain;
        return true;
      }));
      const base = baseFor('Circle','Rope')?.price;
      this.querySelectorAll('[data-cgl-sc-upcharge]').forEach((label) => {
        const price = baseFor(label.dataset.cglScUpcharge,'Rope')?.price;
        label.textContent = base != null && price > base ? `+${this.money(price - base)}` : '';
      });
      const pendantBase = baseFor(this.selected.Pendant,'Rope')?.price;
      this.querySelectorAll('[data-cgl-sc-chain-upcharge]').forEach((label) => {
        const price = baseFor(this.selected.Pendant,label.dataset.cglScChainUpcharge)?.price;
        label.textContent = pendantBase != null && price > pendantBase ? `+${this.money(price - pendantBase)}` : '';
      });
    }

    updateUI() {
      this.photos.forEach((photo,index) => {
        const slot = this.querySelector(`[data-cgl-sc-slot="${index}"]`);
        const image = this.querySelector(`[data-cgl-sc-slot-image="${index}"]`);
        slot?.classList.toggle('has-photo',Boolean(photo.url));
        slot?.classList.toggle('is-complete',photo.confirmed);
        slot?.classList.toggle('is-active',this.photoEditing && index === this.activeIndex);
        if (image && photo.url && image.src !== photo.url) image.src = photo.url;
        this.setText(`[data-cgl-sc-slot-label="${index}"]`,photo.confirmed ? 'Tap to edit' : photo.file ? 'Finish photo' : 'Add photo');
      });
      const ready = this.isReady();
      const count = this.confirmedCount();
      const detail = `${this.color} · ${this.selected.Chain} · ${this.length}`;
      const title = `${this.selected.Pendant} Spinning Pendant`;
      const price = this.currentVariant?.price ?? 0;
      this.setText('[data-cgl-sc-dock-title]',title);
      this.setText('[data-cgl-sc-recap-title]',title);
      this.setText('[data-cgl-sc-dock-detail]',detail);
      this.setText('[data-cgl-sc-recap-detail]',detail);
      this.setText('[data-cgl-sc-dock-price]',this.money(price));
      this.setText('[data-cgl-sc-price]',this.money(price));
      this.setText('[data-cgl-sc-sticky-price]',this.money(price));
      this.setText('[data-cgl-sc-intro-price]',this.money(price));
      this.setText('[data-cgl-sc-delivery-date]',this.deliveryEstimate());
      const compare = Number(this.currentVariant?.compare_at_price || 0);
      const compareElement = this.querySelector('[data-cgl-sc-intro-compare]');
      const saleElement = this.querySelector('[data-cgl-sc-intro-sale]');
      if (compareElement) {
        compareElement.hidden = compare <= price;
        compareElement.textContent = compare > price ? this.money(compare) : '';
      }
      if (saleElement) saleElement.hidden = compare <= price;
      this.setText('[data-cgl-sc-sticky-status]',ready ? 'Both sides ready' : `Add ${2 - count} photo${2 - count === 1 ? '' : 's'}`);
      this.querySelectorAll('[data-cgl-sc-atc],[data-cgl-sc-sticky-atc]').forEach((button) => { button.disabled = !ready || this.loading; });
      this.updateGating();
      const recap = this.querySelector('[data-cgl-sc-recap]');
      if (recap) recap.hidden = !ready;
      if (ready) this.renderRecap();
    }

    updateGating() {
      const choicesReady = this.pendantChosen && this.colorChosen;
      const photosReady = this.confirmedCount() === 2;
      const photoSection = this.querySelector('[data-cgl-sc-step="photos"]');
      const chainSection = this.querySelector('[data-cgl-sc-step="chain"]');
      const pendantSection = this.querySelector('[data-cgl-sc-step="pendant"]');
      pendantSection?.classList.toggle('is-complete',choicesReady);
      photoSection?.classList.toggle('is-complete',photosReady);
      photoSection?.classList.toggle('is-locked',!choicesReady);
      photoSection?.classList.toggle('is-active',choicesReady && !photosReady);
      photoSection?.setAttribute('aria-disabled',String(!choicesReady));
      chainSection?.classList.toggle('is-locked',!photosReady);
      chainSection?.classList.toggle('is-active',photosReady);
      chainSection?.setAttribute('aria-disabled',String(!photosReady));
      this.querySelectorAll('[data-cgl-sc-slot]').forEach((button,index) => { button.disabled = !choicesReady || (index === 1 && !this.photos[0].confirmed); });
      this.querySelectorAll('[data-cgl-sc-chain],input[name^="cgl-sc-length-"]').forEach((control) => { control.disabled = !photosReady; });
    }

    renderActivePhoto() {
      const photo = this.photos[this.activeIndex];
      if (!this.photoImage || !photo?.url) return;
      this.photoImage.src = photo.url;
      const imageAspect = Number(photo.aspect || (this.photoImage.naturalWidth / Math.max(1,this.photoImage.naturalHeight)) || 1);
      const fallbackFrame = this.geometry(this.selected.Pendant);
      const frameAspect = this.photoWindow.clientWidth && this.photoWindow.clientHeight
        ? this.photoWindow.clientWidth / this.photoWindow.clientHeight
        : fallbackFrame.w / Math.max(.0001,fallbackFrame.h);
      if (imageAspect >= frameAspect) {
        this.photoImage.style.width = `${(imageAspect / frameAspect) * 100}%`;
        this.photoImage.style.height = '100%';
      } else {
        this.photoImage.style.width = '100%';
        this.photoImage.style.height = `${(frameAspect / imageAspect) * 100}%`;
      }
      this.photoImage.style.left = `calc(50% + ${photo.x * 100}%)`;
      this.photoImage.style.top = `calc(50% + ${photo.y * 100}%)`;
      this.photoImage.style.transform = `translate(-50%,-50%) scale(${photo.zoom})`;
    }

    pointerDown(event) {
      if (!this.photoEditing) return;
      this.stage.setPointerCapture?.(event.pointerId);
      this.pointers.set(event.pointerId,{ x:event.clientX,y:event.clientY });
      const points = [...this.pointers.values()];
      if (points.length === 2) this.pinch = { distance:this.distance(points[0],points[1]),zoom:this.photos[this.activeIndex].zoom };
    }

    pointerMove(event) {
      if (!this.photoEditing || !this.pointers.has(event.pointerId)) return;
      event.preventDefault();
      const previous = this.pointers.get(event.pointerId);
      this.pointers.set(event.pointerId,{ x:event.clientX,y:event.clientY });
      const photo = this.photos[this.activeIndex];
      const points = [...this.pointers.values()];
      if (points.length === 2 && this.pinch) photo.zoom = this.clamp(this.pinch.zoom * this.distance(points[0],points[1]) / Math.max(1,this.pinch.distance),1,5);
      else {
        const rect = this.photoWindow.getBoundingClientRect();
        photo.x = this.clamp(photo.x + (event.clientX - previous.x) / Math.max(1,rect.width),-4,4);
        photo.y = this.clamp(photo.y + (event.clientY - previous.y) / Math.max(1,rect.height),-4,4);
      }
      if (!photo.adjusted) this.track('photo_adjusted',{ photo_side:this.sides[this.activeIndex].toLowerCase() });
      photo.adjusted = true; this.gesture.hidden = true; this.renderActivePhoto();
    }

    pointerUp(event) {
      this.pointers.delete(event.pointerId);
      if (this.pointers.size < 2) this.pinch = null;
    }

    async renderRecap() {
      if (!this.recapCanvases.length || !this.dockCanvas) return;
      const token = Symbol('spinning-preview');
      this.renderToken = token;
      try {
        const overlay = await this.loadImage(this.assets.overlays?.[this.selected.Pendant]?.[this.color]);
        const chainSource = this.assets.chains?.[this.selected.Chain]?.[this.color];
        let chain = null;
        if (chainSource) {
          try { chain = await this.loadImage(chainSource); }
          catch (error) { if (this.debug) console.warn('[CustomGLD spinning] chain preview failed',error); }
        }
        if (this.renderToken !== token) return;
        this.recapCanvases.forEach((canvas,index) => this.renderSquare(canvas,this.photos[index],overlay,chain));
        this.renderSquare(this.dockCanvas,this.photos[this.lastConfirmedIndex],overlay,chain);
      } catch (error) { if (this.debug) console.warn('[CustomGLD spinning] preview render failed',error); }
    }

    renderSquare(canvas,photo,overlay,chain) {
      const context = canvas.getContext('2d'); if (!context) return;
      const size = Math.min(canvas.width,canvas.height);
      context.clearRect(0,0,canvas.width,canvas.height); context.fillStyle = '#1d1b20'; context.fillRect(0,0,canvas.width,canvas.height);
      context.save(); context.translate((canvas.width - size) / 2,(canvas.height - size) / 2); context.scale(size / 720,size / 720);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      if (chain) {
        if (this.color === 'Silver') context.filter = 'grayscale(1) brightness(1.32) contrast(.82)';
        context.drawImage(chain,0,0,720,720);
        context.filter = 'none';
      }
      const artScale = .78;
      const artX = (720 - 720 * artScale) / 2;
      const artY = artX + 58;
      context.translate(artX,artY);
      context.scale(artScale,artScale);
      this.drawPendant(context,photo,overlay,720);
      context.restore();
    }

    drawPendant(context,photo,overlay,size) {
      if (photo?.image) {
        const geometry = this.geometry(this.selected.Pendant);
        const x = geometry.x * size,y = geometry.y * size,w = geometry.w * size,h = geometry.h * size;
        context.save(); this.clipShape(context,this.selected.Pendant,x,y,w,h); context.clip(); this.drawCover(context,photo.image,x,y,w,h,photo); context.restore();
      }
      context.save();
      context.globalCompositeOperation = 'screen';
      context.drawImage(overlay,0,0,size,size);
      context.restore();
    }

    geometry(shape) {
      return {
        'Circle':{ x:.248,y:.397,w:.50,h:.50 },
        'Circle XL':{ x:.224,y:.35,w:.552,h:.552 },
        'Heart':{ x:.263,y:.404,w:.493,h:.428 },
        'Rectangle':{ x:.378,y:.391,w:.264,h:.516 }
      }[shape] || { x:.248,y:.397,w:.50,h:.50 };
    }

    clipShape(context,shape,x,y,w,h) {
      context.beginPath();
      if (shape === 'Heart') {
        context.moveTo(x + w / 2,y + h);
        context.bezierCurveTo(x + w * .12,y + h * .72,x,y + h * .43,x + w * .08,y + h * .23);
        context.bezierCurveTo(x + w * .18,y,x + w * .42,y + h * .02,x + w / 2,y + h * .20);
        context.bezierCurveTo(x + w * .58,y + h * .02,x + w * .82,y,x + w * .92,y + h * .23);
        context.bezierCurveTo(x + w,y + h * .43,x + w * .88,y + h * .72,x + w / 2,y + h);
      } else if (shape === 'Rectangle') context.roundRect(x,y,w,h,Math.min(w,h) * .04);
      else context.ellipse(x + w / 2,y + h / 2,w / 2,h / 2,0,0,Math.PI * 2);
      context.closePath();
    }

    drawCover(context,image,x,y,width,height,state) {
      const scale = Math.max(width / image.naturalWidth,height / image.naturalHeight) * state.zoom;
      const drawWidth = image.naturalWidth * scale,drawHeight = image.naturalHeight * scale;
      context.drawImage(image,x + (width - drawWidth) / 2 + state.x * width,y + (height - drawHeight) / 2 + state.y * height,drawWidth,drawHeight);
    }

    async addToCart() {
      if (this.loading) return;
      if (!this.isReady()) {
        this.showError(this.photoError,'Please upload and confirm both photos.');
        this.querySelector('[data-cgl-sc-photos-card]')?.scrollIntoView({ behavior:'smooth',block:'center' });
        return;
      }
      if (!this.currentVariant) {
        this.showError(this.formError,'This pendant and chain combination is not available. Check the Shopify variants.');
        return;
      }
      this.loading = true; this.classList.add('is-loading'); this.hideError(this.formError); this.updateUI(); this.setProgress(5); this.setText('[data-cgl-sc-buy-status]','Preparing your necklace…'); this.querySelector('[data-cgl-sc-buy-status]')?.removeAttribute('hidden'); this.track('add_to_cart_clicked');
      try {
        const [preview,...production] = await Promise.all([this.makePreviewFile(),...this.photos.map((photo) => photo.productionPromise || photo.production || photo.file)]);
        const form = new FormData();
        form.append('id',this.currentVariant.id); form.append('quantity','1');
        form.append('properties[Color]',this.color); form.append('properties[Length]',this.length);
        form.append('properties[Estimated delivery]',this.deliveryEstimate());
        form.append('properties[Preview]',preview,preview.name);
        production.forEach((file,index) => form.append(`properties[_${this.sides[index]} Photo]`,file,file.name));
        form.append('properties[_Customizer Version]','Spinning V2');
        form.append('properties[_Customization ID]',this.customizationId);
        form.append('sections','cart-drawer,cart-icon-bubble'); form.append('sections_url',location.pathname);
        const result = await this.upload(form);
        this.setProgress(100); this.track('add_to_cart_success'); this.refreshCart(result);
        if (this.closest('[data-cgl-offer-builder-host]')) {
          this.loading = false; this.classList.remove('is-loading');
          this.dispatchEvent(new CustomEvent('cgl:embedded-item-added',{ bubbles:true,detail:{ productType:'spinning_necklace',productId:this.dataset.productId,variantId:this.currentVariant.id,cartResult:result } }));
          return;
        }
        window.setTimeout(() => { location.href = `${window.Shopify?.routes?.root || '/'}cart`; },350);
      } catch (error) {
        this.showError(this.formError,error.message || 'Something went wrong. Please try again.');
        this.track('add_to_cart_error',{ error_message:String(error.message || '').slice(0,80) });
        this.loading = false; this.classList.remove('is-loading'); this.querySelector('[data-cgl-sc-buy-status]')?.setAttribute('hidden',''); this.updateUI();
      }
    }

    async makePreviewFile() {
      await this.renderRecap();
      const canvas = document.createElement('canvas'); canvas.width = 1040; canvas.height = 520;
      const context = canvas.getContext('2d'); context.fillStyle = '#1d1b20'; context.fillRect(0,0,canvas.width,canvas.height);
      this.recapCanvases.forEach((side,index) => context.drawImage(side,index * 520,0,520,520));
      const blob = await new Promise((resolve,reject) => canvas.toBlob((value) => value ? resolve(value) : reject(new Error('Could not create your spinning pendant preview.')),'image/jpeg',.84));
      return new File([blob],`customgld-spinning-preview-${Date.now()}.jpg`,{ type:'image/jpeg' });
    }

    async optimizePhoto(file,image) {
      const maxEdge = Math.max(image.naturalWidth,image.naturalHeight);
      if (file.size <= 2.5 * 1024 * 1024 && maxEdge <= 2400) return file;
      const scale = Math.min(1,2400 / maxEdge);
      const canvas = document.createElement('canvas'); canvas.width = Math.round(image.naturalWidth * scale); canvas.height = Math.round(image.naturalHeight * scale);
      const context = canvas.getContext('2d',{ alpha:false }); if (!context) return file;
      context.fillStyle = '#fff'; context.fillRect(0,0,canvas.width,canvas.height); context.drawImage(image,0,0,canvas.width,canvas.height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve,'image/jpeg',.88)); canvas.width = canvas.height = 1;
      return blob ? new File([blob],`${this.cleanName(file.name)}-print.jpg`,{ type:'image/jpeg' }) : file;
    }

    upload(form) {
      return new Promise((resolve,reject) => {
        const request = new XMLHttpRequest(); request.open('POST',`${window.Shopify?.routes?.root || '/'}cart/add.js`); request.setRequestHeader('X-Requested-With','XMLHttpRequest'); request.responseType = 'json';
        request.upload.addEventListener('progress',(event) => { if (event.lengthComputable) this.setProgress(8 + event.loaded / event.total * 84); });
        request.onload = () => request.status >= 200 && request.status < 300 ? resolve(request.response) : reject(new Error(request.response?.description || 'Shopify could not add this item.'));
        request.onerror = () => reject(new Error('The upload was interrupted. Please check your connection.')); request.send(form);
      });
    }

    refreshCart(result) {
      const parser = new DOMParser();
      Object.entries(result?.sections || {}).forEach(([id,html]) => {
        if (!html) return; const current = document.getElementById(id); if (!current) return;
        const replacement = parser.parseFromString(html,'text/html').getElementById(id); if (replacement) current.innerHTML = replacement.innerHTML;
      });
    }

    initSticky() {
      if (!this.buy || !this.sticky || !('IntersectionObserver' in window)) return;
      let seen = false;
      this.stickyObserver = new IntersectionObserver((entries) => {
        const entry = entries[0]; if (entry.isIntersecting) seen = true;
        const visible = seen && !entry.isIntersecting && entry.boundingClientRect.top < 0 && !this.photoEditing;
        this.sticky.classList.toggle('is-visible',visible); this.sticky.setAttribute('aria-hidden',String(!visible));
      },{ threshold:.1 });
      this.stickyObserver.observe(this.buy);
    }

    initDock() {
      const dock = this.querySelector('[data-cgl-sc-dock]');
      if (!dock) return;
      this.updateDock = () => {
        this.renderActivePhoto();
        const boundary = this.productIntro?.getBoundingClientRect().bottom ?? this.getBoundingClientRect().top;
        const visible = boundary <= 0 && !this.photoEditing;
        this.classList.toggle('is-dock-visible',visible);
        dock.setAttribute('aria-hidden',String(!visible));
      };
      this.onDockScroll = () => {
        if (this.dockFrame) return;
        this.dockFrame = requestAnimationFrame(() => {
          this.dockFrame = 0;
          this.updateDock();
        });
      };
      window.addEventListener('scroll',this.onDockScroll,{ passive:true });
      window.addEventListener('resize',this.onDockScroll,{ passive:true });
      this.updateDock();
    }

    confirmedCount() { return this.photos.filter((photo) => photo.confirmed).length; }
    isReady() { return this.pendantChosen && this.colorChosen && this.confirmedCount() === 2 && Boolean(this.currentVariant?.available); }
    selectGroup(selector,active) { this.querySelectorAll(selector).forEach((button) => { const selected = button === active; button.classList.toggle('is-selected',selected); button.setAttribute('aria-checked',String(selected)); }); }
    distance(a,b) { return Math.hypot(a.x - b.x,a.y - b.y); }
    clamp(value,min,max) { return Math.min(max,Math.max(min,value)); }
    handle(value='') { return value.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,''); }
    cleanName(name='photo') { return name.replace(/\.[^.]+$/,'').replace(/[^a-z0-9_-]+/gi,'-').slice(0,60); }
    setText(selector,text) { const element = this.querySelector(selector); if (element) element.textContent = text; }
    setProgress(value) { const bar = this.querySelector('[data-cgl-sc-progress]'); if (bar) bar.style.width = `${this.clamp(value,0,100)}%`; }
    showError(element,message) { if (!element) return; element.textContent = message; element.hidden = false; }
    hideError(element) { if (element) element.hidden = true; }
    readJson(selector) { try { return JSON.parse(this.querySelector(selector)?.textContent || '{}'); } catch { return {}; } }
    money(cents) { try { return new Intl.NumberFormat(undefined,{ style:'currency',currency:window.Shopify?.currency?.active || 'USD',maximumFractionDigits:0 }).format((Number(cents) || 0) / 100); } catch { return `$${((Number(cents) || 0) / 100).toFixed(0)}`; } }
    loadImage(source,crossOrigin=true) { return new Promise((resolve,reject) => { if (!source) return reject(new Error('Image source is missing.')); const image = new Image(); if (crossOrigin) image.crossOrigin = 'anonymous'; image.onload = () => resolve(image); image.onerror = reject; image.src = source; }); }
    makeId(prefix) { return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2,9)}`; }
    getSessionId() { try { const key='cgl_session_id'; let id=sessionStorage.getItem(key); if (!id) { id=this.makeId('cgls'); sessionStorage.setItem(key,id); } return id; } catch { return this.makeId('cgls'); } }
    deliveryEstimate() {
      const min = Number(this.dataset.deliveryMin || 17),max = Number(this.dataset.deliveryMax || 21);
      const format = (date) => date.toLocaleDateString('en-US',{ month:'short',day:'numeric' });
      const first = new Date(); first.setDate(first.getDate() + min);
      const last = new Date(); last.setDate(last.getDate() + max);
      return `${format(first)}–${format(last)}`;
    }
    track(name,extra={}) { const payload={ cgl_session_id:this.sessionId,cgl_customization_id:this.customizationId,product_id:this.dataset.productId,variant_id:this.currentVariant?.id,pendant_type:this.selected.Pendant,color:this.color,chain_type:this.selected.Chain,chain_length:this.length,photos_uploaded:this.photos.filter((photo) => photo.file).length,photos_confirmed:this.confirmedCount(),price:(this.currentVariant?.price || 0)/100,currency:window.Shopify?.currency?.active || 'USD',occurred_at:new Date().toISOString(),...extra }; window.dataLayer?.push({ event:`cgl_spinning_${name}`,...payload }); if (this.debug) console.info(`[CustomGLD] cgl_spinning_${name}`,payload); }
  }

  customElements.define('cgl-spinning-customizer',CglSpinningCustomizer);
})();